import { InformationCircleIcon } from '@heroicons/react/24/outline';
import { ChevronDownIcon, ChevronRightIcon, PencilSquareIcon } from '@heroicons/react/24/solid';
import clsx from 'clsx';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  initialMapDatasetIds,
  LAYER_CONFIGS,
  LAYER_GROUPS,
  vectorTileLayerIds,
} from '../constants/general';
import { filterSelector } from '../store/selectors/filters';
import { zoomSelector } from '../store/selectors/map';
import { LayerLoader, SectionLoader } from './general/Loaders';
import { LayerTooltip } from './map/Tooltips';
import { trackEvent, SELECT_LAYER, LAYER_OPACITY_CHANGE } from '../utils/googleAnalytics';

export const LayerSelection = ({ layerManager, availableLayers, structureData }) => {
  const {
    layers,
    layerVisibility,
    layerOpacity,
    loadingStates,
    errors,
    toggleLayerVisibility,
    updateLayerOpacity,
  } = layerManager;

  const filterInfo = useSelector(filterSelector);

  const [expandedOpacityLayers, setExpandedOpacityLayers] = useState(new Set());
  // Groups are expanded unless explicitly collapsed
  const [expandedDropdowns, setExpandedDropdowns] = useState({});

  const [structuresLayers, setStructuresLayers] = useState([]);
  const zoomSelectorInfo = useSelector(zoomSelector);
  const [disabled, setDisabled] = useState([]);

  const toggleDropdown = dropdown => {
    setExpandedDropdowns(prev => ({
      ...prev,
      [dropdown]: !(prev[dropdown] ?? true),
    }));
  };

  useEffect(() => {
    let newStructuresLayers = Array.from(
      new Set([...availableLayers, ...Object.keys(layers)]),
    ).filter(layerId => initialMapDatasetIds.includes(layerId));
    setStructuresLayers(newStructuresLayers);
  }, [availableLayers, layers]);

  useEffect(() => {
    if (zoomSelectorInfo <= 17) {
      setDisabled(prev => [...prev, 'structureFootprints', 'contours2ftTile']);
    } else {
      setDisabled(prev =>
        prev.filter(layer => layer !== 'structureFootprints' && layer !== 'contours2ftTile'),
      );
    }
  }, [zoomSelectorInfo]);

  // Structure layers are available once loaded; all others unless they errored
  const isLayerAvailable = layerId =>
    initialMapDatasetIds.includes(layerId) ? structuresLayers.includes(layerId) : !errors[layerId];

  const getAvailableLayers = group =>
    group.items.flatMap(item =>
      typeof item === 'string' ? (isLayerAvailable(item) ? [item] : []) : getAvailableLayers(item),
    );

  const isStructuresLayersLoading = initialMapDatasetIds.some(layerId => loadingStates[layerId]);
  const hasStructuresLayers = structuresLayers.length > 0;

  const layerCheckbox = (layerId, showStatus = true) => {
    const hasData = layers[layerId];
    const isVisible = layerVisibility[layerId];
    const currentOpacity = layerOpacity[layerId] || 0.7;
    const isLoading = vectorTileLayerIds.includes(layerId) ? false : loadingStates[layerId];
    const hasError = errors[layerId];
    const isOpacityExpanded = expandedOpacityLayers.has(layerId);

    const errorMessage =
      hasError?.status === 404 || hasError?.originalStatus === 404 ? 'No data' : 'Error';

    const handleOpacityChange = e => {
      const newOpacity = parseFloat(e.target.value);

      trackEvent(LAYER_OPACITY_CHANGE, { layerId, newOpacity });
      updateLayerOpacity(layerId, newOpacity);
    };

    const toggleOpacitySlider = () => {
      setExpandedOpacityLayers(prev => {
        const newSet = new Set(prev);
        if (newSet.has(layerId)) {
          newSet.delete(layerId);
        } else {
          newSet.add(layerId);
        }
        return newSet;
      });
    };

    const layerName = LAYER_CONFIGS[layerId]?.name || layerId;
    const lastSpaceIndex = layerName.lastIndexOf(' ');
    const layerNameStart = layerName.slice(0, lastSpaceIndex + 1);
    const layerNameEnd = layerName.slice(lastSpaceIndex + 1);

    return (
      <div key={layerId} className="mb-4">
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id={layerId}
            checked={isVisible || false}
            onChange={() => {
              trackEvent(SELECT_LAYER, { layerId });
              toggleLayerVisibility(layerId);
            }}
            disabled={isLoading || hasError || !hasData || disabled.includes(layerId)}
            className="h-4 w-4 appearance-none border border-primary checked:border checked:border-primary checked:bg-teal disabled:border-fossilGrey disabled:opacity-50"
          />

          <label htmlFor={layerId} className="flex cursor-pointer items-center gap-1.5 text-sm">
            {/* Keep the icon and check attached to the last word so they trail the text when the name wraps */}
            <span>
              {layerNameStart}
              <span className="whitespace-nowrap">
                {layerNameEnd}
                {LAYER_CONFIGS[layerId]?.description ? (
                  <InformationCircleIcon
                    className="ml-1.5 inline-block h-4 w-4 align-text-bottom"
                    data-tooltip-id={layerId}
                    aria-label={`${layerId}-tooltip`}
                  />
                ) : null}

                {showStatus && hasData && !isLoading && !hasError && (
                  <span className="ml-1.5 text-xs text-green-500">✓</span>
                )}
              </span>
            </span>

            {isLoading && <LayerLoader />}

            {/* For some reason between getting large amounts of data and the data loading on fe, all states are undefined. This is to account for that state. */}
            {showStatus &&
              isLoading === undefined &&
              hasError === undefined &&
              hasData === undefined && <LayerLoader />}

            {showStatus && hasError && (
              <span
                className={clsx(
                  errorMessage === 'Error' ? 'text-red-500' : 'text-gray-500',
                  'text-xs',
                )}
              >{`(${errorMessage})`}</span>
            )}
          </label>

          {isVisible && hasData && (
            <button
              onClick={toggleOpacitySlider}
              className="cursor-pointer"
              aria-label="Toggle opacity slider"
            >
              <PencilSquareIcon
                className={clsx('h-4 w-4', isOpacityExpanded ? 'text-teal' : 'text-blue')}
              />
            </button>
          )}
        </div>

        <LayerTooltip layerId={layerId} />

        {/* Opacity slider - only show when layer is visible and pencil icon is clicked */}
        {isVisible && hasData && isOpacityExpanded && (
          <div className="ml-6 flex items-center gap-3">
            <span className="text-xs text-gray-600">Opacity:</span>
            <input
              type="range"
              min="0"
              max="1"
              step="0.1"
              value={currentOpacity}
              onChange={handleOpacityChange}
              className="h-1 w-24 cursor-pointer appearance-none rounded-lg bg-teal [&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:border-0 [&::-moz-range-thumb]:bg-blue [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-blue"
            />
            <span className="w-8 text-xs text-gray-600">{Math.round(currentOpacity * 100)}%</span>
          </div>
        )}
        {LAYER_CONFIGS[layerId]?.warningText ? (
          <span className="ml-6 text-xs text-primary">{LAYER_CONFIGS[layerId]?.warningText}</span>
        ) : null}
      </div>
    );
  };

  const renderGroup = (group, isTopLevel = false) => {
    if (!getAvailableLayers(group)?.length) return null;

    const isExpanded = expandedDropdowns[group.id] ?? true;

    return (
      <div key={group.id} className="mb-4">
        <div className="mb-2 flex items-center gap-1.5">
          <button
            onClick={() => toggleDropdown(group.id)}
            className={clsx(
              'flex items-center gap-2 font-medium text-gray-700 hover:text-gray-900',
              isTopLevel ? 'text-base' : 'text-sm',
            )}
          >
            {isExpanded ? (
              <ChevronDownIcon className="h-4 w-4" />
            ) : (
              <ChevronRightIcon className="h-4 w-4" />
            )}
            {group.name}
          </button>
          {group.description ? (
            <>
              <InformationCircleIcon
                className="h-4 w-4 shrink-0"
                data-tooltip-id={`${group.id}-group`}
                aria-label={`${group.id}-group-tooltip`}
              />
              <LayerTooltip layerId={`${group.id}-group`} content={group.description} />
            </>
          ) : null}
        </div>
        {isExpanded && (
          <div className="ml-6">
            {group.note && (
              <div className="mb-4 pr-4 text-xs">
                <strong>Note:</strong> {group.note}
              </div>
            )}
            {group.items.map(item =>
              typeof item === 'string'
                ? isLayerAvailable(item) && layerCheckbox(item)
                : renderGroup(item),
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="flex h-full flex-col bg-white py-1">
      {filterInfo.selectedCounty ? (
        <>
          {isStructuresLayersLoading && !hasStructuresLayers ? (
            <div className="mb-4">
              <SectionLoader message="Loading layers" />
            </div>
          ) : (
            <div className="flex-1 overflow-y-auto">
              {Object.keys(layers).length ? (
                <div className="mb-8 ml-8 mt-4">
                  {LAYER_GROUPS.map(group => renderGroup(group, true))}
                </div>
              ) : null}
            </div>
          )}
        </>
      ) : (
        !structureData &&
        !isStructuresLayersLoading && (
          <div className="flex-1 overflow-y-auto">
            <p className="mx-6 mb-4 text-center text-gray-500">
              Select a planning area to view points of interest
            </p>
          </div>
        )
      )}
    </div>
  );
};
